import React, { useState, useEffect } from 'react';
import { agendaService, Agenda, AgendaRequest, professorService, Professor, periodoService, Periodo } from '../services/api';
import DeleteConfirmationModal from './DeleteConfirmationModal';

const Agendas: React.FC = () => {
  const [agendas, setAgendas] = useState<Agenda[]>([]);
  const [professores, setProfessores] = useState<Professor[]>([]);
  const [periodos, setPeriodos] = useState<Periodo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  // Estado de edição removido - agendas são criadas em lote
  const [saving, setSaving] = useState(false);
  const [professorFilter, setProfessorFilter] = useState('');
  const [periodoFilter, setPeriodoFilter] = useState('');
  const [formData, setFormData] = useState({
    selectedProfessores: [] as string[],
    selectedPeriodos: [] as string[]
  });
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    itemName: '',
    onConfirm: () => {},
    isLoading: false
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [agendasData, professoresData, periodosData] = await Promise.all([
        agendaService.getAll(),
        professorService.getAll(),
        periodoService.getAll()
      ]);
      setAgendas(agendasData);
      setProfessores(professoresData);
      setPeriodos(periodosData);
      setError(null);
    } catch (err) {
      setError('Erro ao carregar dados');
      console.error('Erro ao carregar dados:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.selectedProfessores.length === 0 || formData.selectedPeriodos.length === 0) {
      setError('Selecione pelo menos um professor e um período');
      return;
    }

    try {
      setSaving(true);
      setError('');
      setSuccess('');

      // Gerar combinações de professor/período
      const combinacoes: AgendaRequest[] = [];
      
      for (const professorId of formData.selectedProfessores) {
        for (const periodoId of formData.selectedPeriodos) {
          const professor = professores.find(p => p.id!.toString() === professorId);
          const periodo = periodos.find(p => p.id!.toString() === periodoId);
          
          if (professor && periodo) {
            combinacoes.push({
              nome: `${professor.nome} / ${periodo.nome}`,
              professorId: professorId,
              periodoId: periodoId
            });
          }
        }
      }

      // Enviar cada combinação
      await agendaService.createBatch(combinacoes);
      
      setSuccess(`${combinacoes.length} agenda(s) cadastrada(s) com sucesso!`);
      
      // Limpar seleções
      setFormData({
        selectedProfessores: [],
        selectedPeriodos: []
      });
      
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao salvar agendas');
    } finally {
      setSaving(false);
    }
  };

  // Função de edição removida - agendas são criadas em lote

  const handleDelete = (id: number) => {
    const agenda = agendas.find(a => a.id === id);
    setDeleteModal({
      isOpen: true,
      title: 'Excluir Agenda',
      message: 'Esta ação não pode ser desfeita.',
      itemName: agenda?.nome || 'agenda',
      onConfirm: () => confirmDeleteAgenda(id),
      isLoading: false
    });
  };

  const confirmDeleteAgenda = async (id: number) => {
    setDeleteModal(prev => ({ ...prev, isLoading: true }));
    try {
      await agendaService.delete(id);
      setSuccess('Agenda excluída com sucesso!');
      loadData();
      setError(null);
      setDeleteModal(prev => ({ ...prev, isOpen: false, isLoading: false }));
    } catch (err) {
      setError('Erro ao excluir agenda');
      console.error('Erro ao excluir agenda:', err);
      setDeleteModal(prev => ({ ...prev, isLoading: false }));
    }
  };

  const filteredAgendas = agendas.filter(agenda =>
    agenda.nome.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const resetForm = () => {
    setFormData({
      selectedProfessores: [],
      selectedPeriodos: []
    });
    setShowForm(false);
    setProfessorFilter('');
    setPeriodoFilter('');
    setError('');
    setSuccess('');
  };

  // Função para alternar seleção
  const toggleSelection = (id: string, type: 'professor' | 'periodo') => {
    if (type === 'professor') {
      setFormData(prev => ({
        ...prev,
        selectedProfessores: prev.selectedProfessores.includes(id)
          ? prev.selectedProfessores.filter(item => item !== id)
          : [...prev.selectedProfessores, id]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        selectedPeriodos: prev.selectedPeriodos.includes(id)
          ? prev.selectedPeriodos.filter(item => item !== id)
          : [...prev.selectedPeriodos, id]
      }));
    }
  };

  // Filtros
  const filteredProfessores = professores.filter(professor =>
    professor.nome.toLowerCase().includes(professorFilter.toLowerCase())
  );

  const filteredPeriodos = periodos.filter(periodo =>
    periodo.nome.toLowerCase().includes(periodoFilter.toLowerCase())
  );

  return (
    <div className="main-content">
      <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>
              <i className="fas fa-calendar-check me-2"></i>
              Gerenciar Agendas
            </h2>
            <button
              className="btn btn-primary"
              onClick={() => setShowForm(true)}
            >
              <i className="fas fa-plus me-2"></i>
              Adicionar Agenda
            </button>
          </div>

          {error && (
            <div className="alert alert-danger alert-dismissible fade show" role="alert">
              {error}
              <button
                type="button"
                className="btn-close"
                onClick={() => setError(null)}
              ></button>
            </div>
          )}

          {success && (
            <div className="alert alert-success alert-dismissible fade show" role="alert">
              {success}
              <button
                type="button"
                className="btn-close"
                onClick={() => setSuccess(null)}
              ></button>
            </div>
          )}

          {showForm && (
            <div className="card mb-4">
              <div className="card-header">
                <h5 className="mb-0">
                  <i className="fas fa-calendar-plus me-2"></i>
                  Adicionar Agenda para Professores
                </h5>
              </div>
              <div className="card-body">
                <form onSubmit={handleSubmit}>
                  <div className="row mb-3">
                    {/* Coluna Professores */}
                    <div className="col-md-6">
                      <label className="form-label">Professores *</label>
                      <div className="mb-2">
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="Filtrar professores..."
                          value={professorFilter}
                          onChange={(e) => setProfessorFilter(e.target.value)}
                        />
                      </div>
                      <div 
                        style={{
                          maxHeight: '300px',
                          overflowY: 'auto',
                          border: '1px solid #dee2e6',
                          borderRadius: '0.375rem',
                          padding: '0.5rem'
                        }}
                      >
                        <div className="row g-2">
                          {filteredProfessores.map((professor) => (
                            <div key={professor.id} className="col-md-6">
                              <div 
                                className={`card shadow-sm border-0 ${
                                  formData.selectedProfessores.includes(professor.id!.toString()) 
                                    ? 'border-success' 
                                    : ''
                                }`}
                                style={{
                                  cursor: 'pointer',
                                  transition: 'all 0.3s ease',
                                  borderRadius: '8px',
                                  border: formData.selectedProfessores.includes(professor.id!.toString()) ? '2px solid #28a745' : '1px solid #e9ecef'
                                }}
                                onClick={() => toggleSelection(professor.id!.toString(), 'professor')}
                                onMouseEnter={(e) => {
                                  if (!formData.selectedProfessores.includes(professor.id!.toString())) {
                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                                    e.currentTarget.style.borderColor = '#28a745';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (!formData.selectedProfessores.includes(professor.id!.toString())) {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
                                    e.currentTarget.style.borderColor = '#e9ecef';
                                  }
                                }}
                              >
                                <div className="card-body p-2 d-flex align-items-center">
                                  <div 
                                    style={{
                                      width: '30px',
                                      height: '30px',
                                      background: formData.selectedProfessores.includes(professor.id!.toString()) ? 'linear-gradient(135deg, #28a745, #1e7e34)' : 'linear-gradient(135deg, #6c757d, #495057)',
                                      borderRadius: '50%',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      color: 'white',
                                      fontSize: '0.8rem',
                                      fontWeight: 'bold',
                                      marginRight: '0.75rem',
                                      flexShrink: 0
                                    }}
                                  >
                                    {professor.nome.charAt(0).toUpperCase()}
                                  </div>
                                  <div className="flex-grow-1">
                                    <small style={{ fontWeight: '600', color: '#2c3e50' }}>
                                      {professor.nome}
                                    </small>
                                  </div>
                                  {formData.selectedProfessores.includes(professor.id!.toString()) && (
                                    <div className="text-success">
                                      <i className="fas fa-check"></i>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Coluna Períodos */}
                    <div className="col-md-6">
                      <label className="form-label">Períodos *</label>
                      <div className="mb-2">
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="Filtrar períodos..."
                          value={periodoFilter}
                          onChange={(e) => setPeriodoFilter(e.target.value)}
                        />
                      </div>
                      <div 
                        style={{
                          maxHeight: '300px',
                          overflowY: 'auto',
                          border: '1px solid #dee2e6',
                          borderRadius: '0.375rem',
                          padding: '0.5rem'
                        }}
                      >
                        <div className="row g-2">
                          {filteredPeriodos.map((periodo) => (
                            <div key={periodo.id} className="col-md-6">
                              <div 
                                className={`card shadow-sm border-0 ${
                                  formData.selectedPeriodos.includes(periodo.id!.toString()) 
                                    ? 'border-primary' 
                                    : ''
                                }`}
                                style={{
                                  cursor: 'pointer',
                                  transition: 'all 0.3s ease',
                                  borderRadius: '8px',
                                  border: formData.selectedPeriodos.includes(periodo.id!.toString()) ? '2px solid #007bff' : '1px solid #e9ecef'
                                }}
                                onClick={() => toggleSelection(periodo.id!.toString(), 'periodo')}
                                onMouseEnter={(e) => {
                                  if (!formData.selectedPeriodos.includes(periodo.id!.toString())) {
                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                                    e.currentTarget.style.borderColor = '#007bff';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (!formData.selectedPeriodos.includes(periodo.id!.toString())) {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
                                    e.currentTarget.style.borderColor = '#e9ecef';
                                  }
                                }}
                              >
                                <div className="card-body p-2 d-flex align-items-center">
                                  <div 
                                    style={{
                                      width: '30px',
                                      height: '30px',
                                      background: formData.selectedPeriodos.includes(periodo.id!.toString()) ? 'linear-gradient(135deg, #007bff, #0056b3)' : 'linear-gradient(135deg, #6c757d, #495057)',
                                      borderRadius: '50%',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      color: 'white',
                                      fontSize: '0.8rem',
                                      fontWeight: 'bold',
                                      marginRight: '0.75rem',
                                      flexShrink: 0
                                    }}
                                  >
                                    <i className="fas fa-clock"></i>
                                  </div>
                                  <div className="flex-grow-1">
                                    <small style={{ fontWeight: '600', color: '#2c3e50' }}>
                                      {periodo.nome}
                                    </small>
                                  </div>
                                  {formData.selectedPeriodos.includes(periodo.id!.toString()) && (
                                    <div className="text-primary">
                                      <i className="fas fa-check"></i>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Resumo das seleções */}
                  {(formData.selectedProfessores.length > 0 || formData.selectedPeriodos.length > 0) && (
                    <div className="alert alert-info mb-3">
                      <h6 className="mb-2">
                        <i className="fas fa-info-circle me-2"></i>
                        Resumo das Seleções
                      </h6>
                      <div className="row">
                        <div className="col-md-6">
                          <strong>Professores selecionados ({formData.selectedProfessores.length}):</strong>
                          <ul className="mb-0 mt-1">
                            {formData.selectedProfessores.map(id => {
                              const professor = professores.find(p => p.id!.toString() === id);
                              return professor ? <li key={id}>{professor.nome}</li> : null;
                            })}
                          </ul>
                        </div>
                        <div className="col-md-6">
                          <strong>Períodos selecionados ({formData.selectedPeriodos.length}):</strong>
                          <ul className="mb-0 mt-1">
                            {formData.selectedPeriodos.map(id => {
                              const periodo = periodos.find(p => p.id!.toString() === id);
                              return periodo ? <li key={id}>{periodo.nome}</li> : null;
                            })}
                          </ul>
                        </div>
                      </div>
                      <div className="mt-2">
                        <strong>Total de agendas a serem criadas: {formData.selectedProfessores.length * formData.selectedPeriodos.length}</strong>
                      </div>
                    </div>
                  )}

                  <div className="d-flex gap-2">
                    <button 
                      type="submit" 
                      className="btn btn-success"
                      disabled={saving || formData.selectedProfessores.length === 0 || formData.selectedPeriodos.length === 0}
                    >
                      {saving ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                          Salvando...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save me-2"></i>
                          Criar Agendas
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={resetForm}
                      disabled={saving}
                    >
                      <i className="fas fa-times me-2"></i>
                      Cancelar
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          <div className="card">
            <div className="card-header">
              <div className="row align-items-center">
                <div className="col-md-6">
                  <h5 className="mb-0">Lista de Agendas</h5>
                </div>
                <div className="col-md-6">
                  <div className="input-group">
                    <span className="input-group-text">
                      <i className="fas fa-search"></i>
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Buscar agendas..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="card-body">
              {loading ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Carregando...</span>
                  </div>
                  <p className="mt-2">Carregando agendas...</p>
                </div>
              ) : filteredAgendas.length === 0 ? (
                <div className="text-center py-4">
                  <i className="fas fa-calendar-check fa-3x text-muted mb-3"></i>
                  <p className="text-muted">
                    {searchTerm ? 'Nenhuma agenda encontrada com o termo pesquisado.' : 'Nenhuma agenda cadastrada ainda.'}
                  </p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead className="table-light">
                      <tr>
                        <th className="id-column">ID</th>
                        <th>Nome da Agenda</th>
                        <th>Professor</th>
                        <th>Período</th>
                        <th style={{width: '80px'}}>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredAgendas.map((agenda) => {
                        // Extrair professor e período do nome
                        const nomeParts = agenda.nome.split(' / ');
                        const professorNome = nomeParts[0] || '-';
                        const periodoNome = nomeParts[1] || '-';
                        
                        return (
                          <tr key={agenda.id}>
                            <td className="id-column">{agenda.id}</td>
                            <td>
                              <strong>{agenda.nome}</strong>
                            </td>
                            <td>
                              <i className="fas fa-user me-2 text-success"></i>
                              {professorNome}
                            </td>
                            <td>
                              <i className="fas fa-clock me-2 text-primary"></i>
                              {periodoNome}
                            </td>
                            <td>
                              <button
                                className="btn btn-outline-danger btn-sm"
                                onClick={() => agenda.id && handleDelete(agenda.id)}
                                title="Excluir"
                              >
                                <i className="fas fa-trash"></i>
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal de Confirmação */}
      <DeleteConfirmationModal
        isOpen={deleteModal.isOpen}
        title={deleteModal.title}
        message={deleteModal.message}
        itemName={deleteModal.itemName}
        onConfirm={deleteModal.onConfirm}
        onClose={() => setDeleteModal(prev => ({ ...prev, isOpen: false }))}
        isLoading={deleteModal.isLoading}
      />
      </div>
    </div>
  );
};

export default Agendas;